import React from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

import BASE_TAG_TYPES from "@src/metadata/tagtypes.mjs";
import baseTagTypeStyles from "@src/metadata/tagtypes.module.styl";

import styles from "./InlineTags.module.styl";

const normalizeTags = (tags) => {
  if (!tags || typeof tags !== "object" || Array.isArray(tags)) {
    return {};
  }

  return tags;
};

const normalizeTagTypes = (tagTypes) => {
  if (!tagTypes || typeof tagTypes !== "object" || Array.isArray(tagTypes)) {
    return {};
  }

  return tagTypes;
};

const cleanKey = (value) => String(value || "").trim();

const cleanValue = (value) => String(value || "").trim();

const normalizeLookupKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getEffectiveTagTypes = (tagTypes) => ({
  ...BASE_TAG_TYPES,
  ...normalizeTagTypes(tagTypes)
});

const buildTagTypeIndex = (tagTypes) => {
  const index = {};

  Object.entries(normalizeTagTypes(tagTypes)).forEach(([key, definition]) => {
    if (!definition || typeof definition !== "object") {
      return;
    }

    const canonical = {
      key,
      ...definition
    };

    const candidates = [
      key,
      definition.label,
      ...(Array.isArray(definition.aliases) ? definition.aliases : [])
    ];

    candidates.forEach((candidate) => {
      const lookup = normalizeLookupKey(candidate);

      if (lookup) {
        index[lookup] = canonical;
      }
    });
  });

  return index;
};

const buildTagOptions = (tagTypes, existingTags) => {
  const existingKeys = new Set(
    Object.keys(existingTags || {}).map((key) => normalizeLookupKey(key))
  );

  return Object.entries(normalizeTagTypes(tagTypes))
    .filter(([key]) => key !== "default")
    .filter(([key]) => !existingKeys.has(normalizeLookupKey(key)))
    .map(([key, definition]) => ({
      key,
      label: definition?.label || key,
      aliases: Array.isArray(definition?.aliases) ? definition.aliases : [],
      logo: definition?.logo || null,
      icon: definition?.icon || null,
      className: definition?.className || baseTagTypeStyles.tagDefault
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const resolveCanonicalTagKey = (rawKey, tagTypeIndex) => {
  const key = cleanKey(rawKey);

  if (!key) {
    return "";
  }

  return tagTypeIndex[normalizeLookupKey(key)]?.key || key;
};

const renderIcon = (IconComponent) => {
  if (!IconComponent) {
    return <LocalOfferIcon fontSize="small" />;
  }

  if (React.isValidElement(IconComponent)) {
    return IconComponent;
  }

  if (typeof IconComponent === "string") {
    return <LocalOfferIcon fontSize="small" />;
  }

  return React.createElement(IconComponent, {
    fontSize: "small"
  });
};

const renderLogo = (logo, label) => {
  if (!logo) {
    return null;
  }

  if (React.isValidElement(logo)) {
    return logo;
  }

  return (
    <img
      src={String(logo)}
      alt={label || "tag"}
      className={baseTagTypeStyles.tagLogo}
      loading="lazy"
    />
  );
};

const renderTagValue = (value) => {
  if (value === true || value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const resolveTagMeta = (key, tagTypeIndex) => {
  const definition = tagTypeIndex[normalizeLookupKey(key)];

  if (!definition) {
    return {
      label: key,
      icon: renderIcon(null),
      logo: null,
      className: baseTagTypeStyles.tagDefault,
      defaultValue: true
    };
  }

  const label = definition.label || key;

  return {
    label,
    icon: definition.logo ? null : renderIcon(definition.icon),
    logo: renderLogo(definition.logo, label),
    className: definition.className || baseTagTypeStyles.tagDefault,
    defaultValue: Object.prototype.hasOwnProperty.call(definition, "defaultValue")
      ? definition.defaultValue
      : true
  };
};

export const InlineTags = ({
  tags,
  onChange,
  tagTypes,
  label = "Tags",
  emptyText = "No tags.",
  className = "",
  disabled = false,
  showHeader = true
}) => {
  const [editing, setEditing] = React.useState(false);
  const [tagKey, setTagKey] = React.useState("");
  const [tagValue, setTagValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [removingKey, setRemovingKey] = React.useState("");
  const [highlightedOption, setHighlightedOption] = React.useState(null);

  const tagInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!editing) {
      return;
    }

    window.requestAnimationFrame(() => {
      tagInputRef.current?.focus();
    });
  }, [editing]);

  const tagObject = normalizeTags(tags);

  const entries = Object.entries(tagObject).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const effectiveTagTypes = React.useMemo(
    () => getEffectiveTagTypes(tagTypes),
    [tagTypes]
  );

  const tagTypeIndex = React.useMemo(
    () => buildTagTypeIndex(effectiveTagTypes),
    [effectiveTagTypes]
  );

  const tagOptions = React.useMemo(
    () => buildTagOptions(effectiveTagTypes, tagObject),
    [effectiveTagTypes, tagObject]
  );

  const resetEditor = () => {
    setEditing(false);
    setTagKey("");
    setTagValue("");
    setHighlightedOption(null);
  };

  const commit = async (nextTags) => {
    if (!onChange) {
      return;
    }

    await onChange(nextTags || {});
  };

  const addResolvedTag = async (rawKey) => {
    const sourceKey = cleanKey(rawKey);

    if (!sourceKey || saving || disabled) {
      return;
    }

    const key = resolveCanonicalTagKey(sourceKey, tagTypeIndex);
    const value = cleanValue(tagValue);
    const meta = resolveTagMeta(key, tagTypeIndex);

    setSaving(true);

    try {
      const resolvedDefaultValue =
        typeof meta.defaultValue === "function"
          ? meta.defaultValue()
          : meta.defaultValue;

      await commit({
        ...tagObject,
        [key]: value ? value : resolvedDefaultValue
      });

      resetEditor();
    } finally {
      setSaving(false);
    }
  };

  const addTag = async () => {
    await addResolvedTag(tagKey);
  };

  const removeTag = async (key) => {
    if (!key || removingKey || disabled) {
      return;
    }

    setRemovingKey(key);

    try {
      const nextTags = { ...tagObject };
      delete nextTags[key];

      await commit(nextTags);
    } finally {
      setRemovingKey("");
    }
  };

  const canEdit = !disabled && Boolean(onChange);
  const showTags = entries.length > 0 || (!showHeader && canEdit && !editing);

  return (
    <span className={`${styles.inlineTags} ${className || ""}`}>
      {showHeader ? (
        <span className={styles.header}>
          <span className={styles.label}>{label}</span>

          {!editing && canEdit ? (
            <Tooltip title="Add tag">
              <IconButton
                size="small"
                className={styles.addButton}
                onClick={() => setEditing(true)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </span>
      ) : null}

      {showTags ? (
        <span className={styles.tagList}>
          {entries.map(([key, value]) => {
            const meta = resolveTagMeta(key, tagTypeIndex);
            const renderedValue = renderTagValue(value);

            return (
              <span
                key={key}
                className={`${styles.tagStamp} ${meta.className || ""}`}
                title={meta.label}
              >
                <span className={styles.tagIcon}>{meta.logo || meta.icon}</span>

                <span className={styles.tagText}>
                  <span className={styles.tagKey}>{meta.label}</span>

                  {renderedValue ? (
                    <span className={styles.tagValue}>{renderedValue}</span>
                  ) : null}
                </span>

                {canEdit ? (
                  <button
                    type="button"
                    className={styles.removeTag}
                    disabled={removingKey === key}
                    onClick={() => removeTag(key)}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            );
          })}

          {!showHeader && canEdit && !editing ? (
            <Tooltip title="Add tag">
              <IconButton
                size="small"
                className={styles.inlineAddButton}
                onClick={() => setEditing(true)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </span>
      ) : emptyText ? (
        <span className={styles.empty}>{emptyText}</span>
      ) : null}

      {editing && canEdit ? (
        <span className={styles.editor}>
          <Autocomplete
            freeSolo
            autoHighlight
            openOnFocus
            selectOnFocus={false}
            clearOnBlur={false}
            handleHomeEndKeys
            size="small"
            disabled={saving}
            options={tagOptions}
            inputValue={tagKey}
            className={styles.tagAutocomplete}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }

              return option.label || option.key || "";
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value || typeof value === "string") {
                return false;
              }

              return option.key === value.key;
            }}
            filterOptions={(options, state) => {
              const q = normalizeLookupKey(state.inputValue);

              if (!q) {
                return options;
              }

              return options.filter((option) => {
                const parts = [
                  option.key,
                  option.label,
                  ...(Array.isArray(option.aliases) ? option.aliases : [])
                ];

                return parts.some((part) =>
                  normalizeLookupKey(part).includes(q)
                );
              });
            }}
            onHighlightChange={(event, option) => {
              setHighlightedOption(option || null);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === "reset") {
                return;
              }

              setTagKey(value || "");
            }}
            onChange={(event, value) => {
              if (!value) {
                return;
              }

              if (typeof value === "string") {
                setTagKey(value);
                return;
              }

              addResolvedTag(value.key);
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <span className={styles.autocompleteOption}>
                  <span
                    className={`${styles.autocompleteOptionIcon} ${
                      option.className || ""
                    }`}
                  >
                    {option.logo
                      ? renderLogo(option.logo, option.label)
                      : renderIcon(option.icon)}
                  </span>

                  <span className={styles.autocompleteOptionText}>
                    <span className={styles.autocompleteOptionLabel}>
                      {option.label}
                    </span>

                    {option.aliases.length ? (
                      <span className={styles.autocompleteOptionAliases}>
                        {option.aliases.join(", ")}
                      </span>
                    ) : null}
                  </span>
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={tagInputRef}
                placeholder="tag"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    resetEditor();
                    return;
                  }

                  if (event.key !== "Enter") {
                    return;
                  }

                  if (highlightedOption) {
                    return;
                  }

                  event.preventDefault();
                  addTag();
                }}
              />
            )}
          />

          <input
            className={styles.valueInput}
            value={tagValue}
            disabled={saving}
            placeholder="value"
            onChange={(event) => setTagValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addTag();
              }

              if (event.key === "Escape") {
                event.preventDefault();
                resetEditor();
              }
            }}
          />

          <Button
            size="small"
            variant="contained"
            className={styles.saveButton}
            disabled={saving || !cleanKey(tagKey)}
            onClick={addTag}
          >
            Add
          </Button>

          <Tooltip title="Cancel">
            <span>
              <IconButton
                size="small"
                className={styles.cancelButton}
                disabled={saving}
                onClick={resetEditor}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ) : null}
    </span>
  );
};

export default InlineTags;